/**
 * Context-aware asset placement for the canvas editor.
 *
 * The sidebar tabs (Backgrounds, Photos, colors) historically hardcoded
 * `addNewNode` — a clicked photo always became a new full-canvas image, never
 * the fill of the shape you had selected. That's the "panel-first" friction.
 *
 * These helpers make placement selection-first / drop-target-first, the way
 * Figma and Canva behave:
 *   - an image dropped on / clicked with a fillable shape selected fills it,
 *   - an image node target is replaced in place,
 *   - with no relevant target it falls back to placing a node (a full-canvas
 *     background or a sized photo at a point).
 *
 * Store-only (no React) so both the sidebar click handlers and the canvas drop
 * handler can route through the exact same resolution.
 */
import type { CanvasEditorStore } from './canvaskit-editor-store';
import type { SceneNode, Paint } from '@/lib/scene-document';
import { generateNodeId } from './canvaskit-mutations';

/** Node types that can take a fill (so an image/color applies to them). */
const FILLABLE_TYPES: ReadonlySet<SceneNode['type']> = new Set([
  'FRAME',
  'RECTANGLE',
  'ELLIPSE',
  'PATH',
  'STAR',
  'POLYGON',
  'TRAPEZOID',
  'SEAL',
]);

export interface PlaceImageOptions {
  /** Explicit target (e.g. the node under a drop point). */
  targetNodeId?: string;
  /** Where to place a new node when there is no fill target (scene coords). */
  atScene?: { x: number; y: number };
  /**
   * Fallback when nothing is targeted: 'background' fills the whole canvas
   * (the classic Backgrounds-tab behavior); 'photo' places a sized image node.
   */
  placeMode?: 'background' | 'photo';
  /** Optional name for a newly created node. */
  name?: string;
}

/**
 * Resolve the fill target: explicit id wins, else the single selected fillable
 * node (or an IMAGE node, which gets replaced), else null.
 */
function resolveFillTarget(store: CanvasEditorStore, targetNodeId?: string): SceneNode | null {
  const s = store.getState();
  const id = targetNodeId ?? (s.selectedIds.size === 1 ? [...s.selectedIds][0] : undefined);
  if (!id) return null;
  const node = s.doc.nodes[id];
  if (!node) return null;
  if (node.type === 'IMAGE' || FILLABLE_TYPES.has(node.type)) return node;
  return null;
}

/**
 * A container frame is the page artboard or any FRAME that lays out / holds
 * content. These are layout containers, not shapes — an image dropped on / over
 * one should become a new child node, never repaint the container's background.
 */
function isContainerFrame(node: SceneNode): boolean {
  if (node.type !== 'FRAME') return false;
  return (
    node.pluginData?.['isArtboard'] === true ||
    node.layoutMode === 'VERTICAL' ||
    node.layoutMode === 'HORIZONTAL' ||
    (node.children?.length ?? 0) > 0
  );
}

/**
 * Whether an image should *fill* the resolved target rather than be placed as a
 * new node. IMAGE targets are always replaced in place. The page artboard is
 * never image-filled (its "background image" is a full-canvas node, see
 * placeBackgroundNode). A drop (explicit targetNodeId from a hit-test) onto a
 * container frame places a new node where it landed instead of repainting that
 * frame — only an empty shape-like frame, or a selection-driven color/pattern
 * fill, repaints a frame.
 */
function isImageFillTarget(node: SceneNode, fromDrop: boolean): boolean {
  if (node.type === 'IMAGE') return true;
  if (node.type === 'FRAME' && node.pluginData?.['isArtboard'] === true) return false;
  if (fromDrop && isContainerFrame(node)) return false;
  return true;
}

/** True when a click/drop here would fill an existing shape rather than place a new node. */
export function hasFillTarget(store: CanvasEditorStore, targetNodeId?: string): boolean {
  const target = resolveFillTarget(store, targetNodeId);
  return target != null && isImageFillTarget(target, targetNodeId != null);
}

/**
 * Apply an image URL context-aware: fill/replace the resolved target, or place
 * a new node when there's nothing to fill.
 */
export function applyImageToTargetOrPlace(
  store: CanvasEditorStore,
  url: string,
  opts: PlaceImageOptions = {}
): void {
  const target = resolveFillTarget(store, opts.targetNodeId);
  const fromDrop = opts.targetNodeId != null;

  if (target && isImageFillTarget(target, fromDrop)) {
    const s = store.getState();
    if (target.type === 'IMAGE') {
      s.updateNodeProp(target.id, { imageSrc: url, imageFit: target.imageFit ?? 'cover' });
    } else {
      const fill: Paint = { type: 'IMAGE', src: url };
      s.updateNodeProp(target.id, { fills: [fill] });
    }
    s.select(target.id);
    return;
  }

  if (opts.placeMode === 'background') {
    placeBackgroundNode(store, url, opts.name);
  } else {
    placePhotoNode(store, url, opts.atScene, opts.name);
  }
}

/** Apply a solid color to the single selected fillable shape, if any. */
export function applySolidToSelection(store: CanvasEditorStore, color: string): boolean {
  const target = resolveFillTarget(store);
  if (!target || target.type === 'IMAGE') return false;
  const fill: Paint = { type: 'SOLID', color };
  store.getState().updateNodeProp(target.id, { fills: [fill] });
  return true;
}

/** Full-canvas cover background image (classic Backgrounds-tab placement). */
function placeBackgroundNode(store: CanvasEditorStore, url: string, name?: string): void {
  const s = store.getState();
  s.addNewNode({
    id: generateNodeId(),
    type: 'IMAGE',
    parentId: null,
    x: 0,
    y: 0,
    width: s.doc.width,
    height: s.doc.height,
    imageSrc: url,
    imageFit: 'cover',
    name: name ?? 'background',
  });
}

/**
 * Place a sized image node at `atScene` (or canvas center). Loads the image to
 * size it to its aspect ratio (cap 300px), mirroring the editor handle's
 * addImage so placement is consistent however it's triggered.
 */
function placePhotoNode(
  store: CanvasEditorStore,
  url: string,
  atScene?: { x: number; y: number },
  name?: string
): void {
  const add = (w: number, h: number) => {
    const s = store.getState();
    const cx = atScene?.x ?? s.doc.width / 2;
    const cy = atScene?.y ?? s.doc.height / 2;
    s.addNewNode({
      id: generateNodeId(),
      type: 'IMAGE',
      parentId: null,
      x: Math.round(cx - w / 2),
      y: Math.round(cy - h / 2),
      width: w,
      height: h,
      imageSrc: url,
      imageFit: 'cover',
      name: name ?? 'image',
    });
  };

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const maxDim = 300;
    const ratio = Math.min(maxDim / img.naturalWidth, maxDim / img.naturalHeight, 1);
    add(Math.round(img.naturalWidth * ratio) || 200, Math.round(img.naturalHeight * ratio) || 200);
  };
  img.onerror = () => add(200, 150);
  img.src = url;
}
